import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { priceFormat, formatOrderDateToLocalTime } from '../../../util/index';
import Loading from '../../LoadingComponent/LoadingComponent';
import { connect } from 'react-redux';
import { getSelectedOrderFromOrdersSearch } from '../../../extractFromState';
import get from 'lodash/get';
import ProtectedData from 'components/common/ProtectedData/ProtectedData';
import * as sensitiveData from 'constants/sensitiveDataTypes';
import {
  OrderDetailsPage,
  TableTd,
  OrderTable,
  Title,
  TitleImage,
  TitleRow,
  TableTr,
  TableTBody,
  Link,
} from '../OrderDetails.styles';

const NA = 'Not Available';
const DEFAULT_CURRENCY = 'USD';

class SimplrOrderDetails extends Component {
  state = {
    isOrderItemsRequesting: true,
  };

  static propTypes = {
    selectedOrder: PropTypes.object.isRequired,
  };

  async componentDidMount() {
    this.setState({
      isOrderItemsRequesting: false,
    });
  }

  renderTrackingNumber(orderItem) {
    const track = this.getShipmentPerOrderItem(orderItem);
    if (track) {
      return (
        <Link href={track.trackingUrl} target="_blank" rel="noopener noreferrer">
          {track.trackingNumber}
        </Link>
      );
    }
  }

  renderShippingAddress(orderItem) {
    const track = this.getShipmentPerOrderItem(orderItem);
    if (track) {
      const shippingAddress = track.shippingAddress;
      const addressDetails = ['firstName', 'address1', 'address2', 'suite', 'city', 'state', 'zip', 'country'];
      const validAddress = [];
      addressDetails.forEach((item) => {
        if (!!shippingAddress[item]) {
          item === 'firstName'
            ? validAddress.push(shippingAddress[item] + ' ' + shippingAddress['lastName'])
            : validAddress.push(shippingAddress[item]);
        }
      });

      return validAddress.join(', ');
    }
  }

  getShipCost() {
    const selectedOrder = this.props.selectedOrder;
    const shipment = selectedOrder.shipment;
    if (shipment) {
      return shipment.reduce((total, { shipCost }) => total + shipCost, 0);
    }

    return 0;
  }

  getShipmentPerOrderItem(orderItem) {
    const shipments = this.props.selectedOrder.shipment;

    if (Array.isArray(shipments)) {
      return shipments.find((shipment) => shipment.shipmentId === orderItem.shipmentId);
    }
  }

  renderComponent() {
    const selectedOrder = this.props.selectedOrder;
    const currencyCode = selectedOrder.currency || DEFAULT_CURRENCY;
    const orderItems = selectedOrder['orderItems'] || [];
    const orderItemsJSX = orderItems.length ? (
      orderItems.reduce((results, orderItem, index) => {
        const itemPrice = orderItem['originalItemUnitPrice']
          ? priceFormat(orderItem['originalItemUnitPrice'], currencyCode)
          : NA;
        const itemTrackingUrl = this.renderTrackingNumber(orderItem);
        const qty = parseInt(orderItem['itemQuantity']);
        results.push(
          <TableTr key={'lineItem' + orderItem.itemId}>
            <TableTd>{index === 0 ? 'Line Items' : ''}</TableTd>
            <TableTd>
              {qty} "{orderItem['itemName']}
              ": {itemPrice}
            </TableTd>
          </TableTr>
        );
        if (orderItem['itemShippingStatus']) {
          results.push(
            <TableTr key={'shipDate' + orderItem.itemId}>
              <TableTd />
              <TableTd style={{ paddingLeft: '3.5rem' }}>
                {' '}
                {orderItem['itemShippingStatus']}: {orderItem['itemShipDate']} {itemTrackingUrl}{' '}
                <ProtectedData
                  data={this.renderShippingAddress(orderItem)}
                  dataType={sensitiveData.shippingAddress}
                  component="SimplrOrderDetails"
                />
              </TableTd>
            </TableTr>
          );
        }

        return results;
      }, [])
    ) : (
      <TableTr>
        <TableTd>Line Items</TableTd>
        <TableTd>{NA}</TableTd>
      </TableTr>
    );
    const orderDiscountAmount = parseFloat(selectedOrder['totalDiscountAppliedToOrder'] || 0);
    const orderRefundAmount = parseFloat(selectedOrder['totalRefundAppliedToOrder'] || 0);
    const orderRefundRequestedAmount = parseFloat(selectedOrder['totalRefundRequestedToOrder'] || 0);
    const orderSubTotalAmount = parseFloat(selectedOrder['orderSubTotal'] || 0);
    const orderTaxAmount = parseFloat(selectedOrder['orderTax'] || 0);
    const total = orderSubTotalAmount + this.getShipCost() + orderTaxAmount - orderRefundAmount;
    const status = selectedOrder.orderStatus;

    // No need general shipping description if we do item shipping individually
    // const shippingDescription = shipment[0].shippingDescription;
    // const shippingDescriptionName = shippingDescription[1] ? shippingDescription[1].value : null;
    // const shippingDescriptionDescription = shippingDescription[2] ? shippingDescription[2].value : null;

    const componentName = 'SimplrOrderDetails';

    const customerEmail = get(selectedOrder, 'customer.email', NA);
    const customerFirstName = get(selectedOrder, 'customer.firstName', '');
    const customerLastName = get(selectedOrder, 'customer.lastName', '');
    const customerFullName = `${customerFirstName} ${customerLastName}`.trim() || NA;

    return (
      <OrderDetailsPage className="order-details-page">
        <TitleRow className="row title-row">
          <div className="col-xs-12">
            <Title className="title">Order Summary</Title>
            <span>
              <TitleImage className="svg title-image" alt="" src="/images/green-clipboard.svg" />
            </span>
          </div>
        </TitleRow>

        <hr />
        <OrderTable className="order-table">
          <TableTBody>
            <TableTr>
              <TableTd>Store Name</TableTd>
              <TableTd>{(selectedOrder['domain'] && selectedOrder['domain'].toUpperCase()) || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Order Number</TableTd>
              <TableTd>{selectedOrder['orderNumber'] || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Order Status</TableTd>
              <TableTd>{status || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Payment Status</TableTd>
              <TableTd>{selectedOrder['paymentStatus'] || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Order ID</TableTd>
              <TableTd>{selectedOrder['orderId'] || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Created At</TableTd>
              <TableTd>{formatOrderDateToLocalTime(selectedOrder['createdAt'], 'simplrOrder')}</TableTd>
            </TableTr>
            {/* <tr>
              <td>Shipping Description</td>
              <td>{shippingDescriptionName || NA}: {shippingDescriptionDescription || NA}</td>
            </tr> */}
            <TableTr>
              <TableTd>Customer Name</TableTd>
              <TableTd>
                <ProtectedData data={customerFullName} dataType={sensitiveData.fullName} component={componentName} />
              </TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Customer Email</TableTd>
              <TableTd>
                <ProtectedData data={customerEmail} dataType={sensitiveData.email} component={componentName} />
              </TableTd>
            </TableTr>
            {orderItemsJSX}
            <TableTr>
              <TableTd>Discount</TableTd>
              <TableTd>{priceFormat(orderDiscountAmount, currencyCode)}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Subtotal</TableTd>
              <TableTd>{selectedOrder['orderSubTotal'] ? priceFormat(orderSubTotalAmount, currencyCode) : NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipping</TableTd>
              <TableTd>{selectedOrder['shipment'] ? priceFormat(this.getShipCost(), currencyCode) : NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Refund Requested</TableTd>
              <TableTd>{priceFormat(orderRefundRequestedAmount, currencyCode)}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Refund Applied</TableTd>
              <TableTd>{priceFormat(orderRefundAmount, currencyCode)}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Tax</TableTd>
              <TableTd>{selectedOrder['orderTax'] ? priceFormat(selectedOrder['orderTax'], currencyCode) : NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Total</TableTd>
              <TableTd>{priceFormat(total, currencyCode)}</TableTd>
            </TableTr>
          </TableTBody>
        </OrderTable>
      </OrderDetailsPage>
    );
  }

  render() {
    if (this.state.isOrderItemsRequesting) {
      return <Loading label="Loading order information..." />;
    }

    return this.renderComponent();
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrderFromOrdersSearch(state),
});

export default connect(mapStateToProps, null)(SimplrOrderDetails);
